import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { socket, isConnected, reconnect } from '../utils/socket';

const GameSetup: React.FC = () => {
  const { t } = useTranslation();
  const [gameLinks, setGameLinks] = useState<{ master: string; teamA: string; teamB: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(!isConnected());
  const [financeMode, setFinanceMode] = useState(false);

  useEffect(() => {
    const handleConnect = () => {
      console.log('Connected to server');
      setConnecting(false);
      setError(null);
    };

    const handleDisconnect = () => {
      console.log('Disconnected from server');
      setConnecting(true);
      setError('Disconnected from server');
    };

    const handleGameCreated = (data: { gameId: string; links: { master: string; teamA: string; teamB: string } }) => {
      console.log('Game created:', data);
      setGameLinks(data.links);
      setError(null);
    };

    const handleError = (errorMessage: string) => {
      console.error('Server error:', errorMessage);
      setError(errorMessage);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('gameCreated', handleGameCreated);
    socket.on('error', handleError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('gameCreated', handleGameCreated);
      socket.off('error', handleError);
    };
  }, []);

  const handleCreateGame = () => {
    if (!isConnected()) {
      setError('Not connected to server');
      reconnect();
      return;
    }

    console.log('Creating game...', { financeMode });
    socket.emit('createGame', { financeMode });
  };

  // Fonseca pour formater correctement les URLs
  const formatURL = (path: string) => {
    // Enlève les doublons de /starwars si nécessaire
    return path.startsWith('/starwars/') ? path : `/starwars${path}`;
  };

  if (connecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
        <h1 className="text-xl md:text-2xl font-bold mb-8">{t('gameSetup.title')}</h1>
        <p className="text-xl text-blue-600 dark:text-blue-400">{t('connecting')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
        <h1 className="text-xl md:text-2xl font-bold mb-8">{t('gameSetup.title')}</h1>
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => {
            setError(null);
            reconnect();
          }}
          className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold py-2 px-4 rounded shadow-md hover:shadow-lg transition-all"
        >
          {t('tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
      <h1 className="text-xl md:text-2xl font-bold mb-8 text-center px-4">{t('gameSetup.title')}</h1>
      {!gameLinks ? (
        <div className="flex flex-col items-center gap-6">
          <button
            onClick={handleCreateGame}
            className="bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all"
          >
            {t('gameSetup.createGame')}
          </button>

          <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <span className="text-sm font-medium">{t('gameSetup.financeMode')}</span>
            <button
              onClick={() => setFinanceMode(!financeMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${financeMode ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${financeMode ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 mx-4 max-w-lg w-full">
          <p className="text-xl text-green-600 dark:text-green-400 font-semibold text-center">{t('gameSetup.gameCreated')}</p>
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-semibold mb-1 text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('gameSetup.gameMasterLink')}</p>
              <button
                onClick={() => window.open(formatURL(gameLinks.master), '_blank')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer break-all text-sm text-left w-full"
              >
                {window.location.origin + formatURL(gameLinks.master)}
              </button>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-semibold mb-1 text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('gameSetup.teamALink')}</p>
              <button
                onClick={() => window.open(formatURL(gameLinks.teamA), '_blank')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer break-all text-sm text-left w-full"
              >
                {window.location.origin + formatURL(gameLinks.teamA)}
              </button>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="font-semibold mb-1 text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('gameSetup.teamBLink')}</p>
              <button
                onClick={() => window.open(formatURL(gameLinks.teamB), '_blank')}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer break-all text-sm text-left w-full"
              >
                {window.location.origin + formatURL(gameLinks.teamB)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameSetup;
